import { ClientError, GraphQLClient } from 'graphql-request';
import {
  PartialAppContext,
  SsrAppContext
} from '../../../shared/source/components/app-context/app-context.tsx';
import {
  IGraphQLQueryError,
  IGraphQLResponse
} from '../../../shared/source/components/graphql/graphql-service.ts';
import { GqlQuery } from '../../../shared/source/components/graphql/gql-tag.ts';
import { buildQuery } from '../../../shared/source/components/graphql/build-query.ts';

/**
 * Slimmed down version of the frontend GraphQL service for SSR.
 */
export class GraphQLService {
  private client: GraphQLClient;

  constructor(
    private appContext: PartialAppContext<SsrAppContext, 'logger'>,
    private endpoint: string = 'http://sk-stage.nomad.gutefrage.team/gm/graphql/app'
  ) {
    this.client = new GraphQLClient(this.endpoint, {
      credentials: 'same-origin'
    });
  }

  /**
   * Send a GraphQL query or mutation.
   */
  public query = <T, E extends IGraphQLQueryError, V = object>(options: {
    query: string | GqlQuery;
    variables?: V;
  }): Promise<IGraphQLResponse<T, E>> => {
    const query = buildQuery(options.query);
    // Do the request
    return this.client
      .request<T, V>(query, options.variables)
      .then(response => ({ data: response }))
      .catch((error: ClientError) => {
        if (error.response) {
          const response = error.response as IGraphQLResponse<T, E>;

          // Reject if not 200
          if (response.status !== 200) {
            this.onUnexpectedError(`Error code ${response.status}`, query);
            return Promise.reject(error);
          }

          if (response.errors) {
            if (!response.errors[0]?.reason) {
              this.onUnexpectedError(
                `Error code ${response.status}: ${JSON.stringify(response.errors)}`,
                query
              );
            }
          }

          return response;
        } else {
          this.onUnexpectedError(error, query);
          return Promise.reject(error);
        }
      });
  };

  /**
   * Shall be called when a query fails due to network problems or status codes other than 200.
   * Logs the error and displays a toast to the user.
   *
   * @param error    the error message
   * @param options   additional options provided with this query
   */
  private onUnexpectedError(error: unknown, query: string): void {
    this.appContext.logger.error(
      `GraphQL request failed with error: "${JSON.stringify(error)}"\nfor query: "${query}"`
    );
  }
}
